// How old is the bundle this tab is running?
//
// Sentry tags each event with the app version, but that label is bumped by
// hand and can stay put across many deploys (2026.2.1 was the live label
// from February through July). Working out how stale a reporting client
// really was took release archaeology against stack-frame line numbers
// (compliance-log#193). The build timestamp compiled into the bundle
// answers that directly: every event carries the reporter's release age,
// and the update prompt can stop being polite once the bundle is old
// enough that its bugs have long since been fixed.

// ISO-8601 timestamp injected at build time (vite.config.js).
export const BUILD_TIME = __BUILD_TIME__;

const DAY_MS = 24 * 60 * 60 * 1000;

// Past this age a waiting update is applied via a blocking prompt rather
// than the dismissable Snackbar. One month: long enough that an ordinary
// tab left open over a weekend or a vacation never sees it, short enough
// that a client cannot sit a full release cycle behind indefinitely.
export const STALE_RELEASE_MS = 30 * DAY_MS;

/**
 * @param {string} [builtAt] - ISO timestamp of the build
 * @returns {number|null} the build time in epoch ms, or null when unknown
 */
export function getBuildTimeMs(builtAt = BUILD_TIME) {
  if (typeof builtAt !== 'string' || builtAt === '') {
    return null;
  }
  const ms = Date.parse(builtAt);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Age of the running bundle. Clamped at zero: a client whose clock runs
 * behind the build machine is "fresh", not negative.
 *
 * @param {number} [now] - epoch ms
 * @param {string} [builtAt] - ISO timestamp of the build
 * @returns {number|null} age in ms, or null when the build time is unknown
 */
export function getReleaseAgeMs(now = Date.now(), builtAt = BUILD_TIME) {
  const builtMs = getBuildTimeMs(builtAt);
  if (builtMs === null) {
    return null;
  }
  return Math.max(0, now - builtMs);
}

/**
 * @param {number} [now] - epoch ms
 * @param {string} [builtAt] - ISO timestamp of the build
 * @returns {number|null} whole days since the build, or null when unknown
 */
export function getReleaseAgeDays(now = Date.now(), builtAt = BUILD_TIME) {
  const ageMs = getReleaseAgeMs(now, builtAt);
  return ageMs === null ? null : Math.floor(ageMs / DAY_MS);
}

/**
 * Whether the running bundle is old enough to force an update through.
 * An unknown build time is never stale: the blocking prompt must not fire
 * on a bundle that simply lacks the timestamp.
 *
 * @param {number} [now] - epoch ms
 * @param {string} [builtAt] - ISO timestamp of the build
 * @param {number} [thresholdMs]
 * @returns {boolean}
 */
export function isReleaseStale(
  now = Date.now(),
  builtAt = BUILD_TIME,
  thresholdMs = STALE_RELEASE_MS,
) {
  const ageMs = getReleaseAgeMs(now, builtAt);
  return ageMs !== null && ageMs >= thresholdMs;
}

/**
 * Sentry `beforeSend` step: tag the event with the reporter's release age.
 *
 * Computed at capture time rather than at init so a tab that has been open
 * for weeks reports the age at the moment of the error, not at page load.
 * Tags (not context) so Sentry can search and filter on them:
 * `release_age_days:>30`.
 *
 * @param {object} event - the Sentry event
 * @param {number} [now] - epoch ms
 * @param {string} [builtAt] - ISO timestamp of the build
 * @returns {object} the same event, tagged
 */
export function tagReleaseAge(event, now = Date.now(), builtAt = BUILD_TIME) {
  const ageDays = getReleaseAgeDays(now, builtAt);
  if (ageDays === null) {
    return event;
  }
  event.tags = {
    ...event.tags,
    release_built_at: builtAt,
    release_age_days: ageDays,
  };
  return event;
}
